import React from 'react';
import {flatten, range} from 'lodash';
import {useSelector} from 'react-redux';
import {useFormikContext} from 'formik';

import {possibleMajors} from 'transfer-student/utils';
import {Store} from 'src/types/store';
import {parseIntOrNull} from 'shared/utils';

import Icon from 'shared/components/icon';
import {CollegeCourseForm as CollegeCourseFormType} from 'shared/portfolio/components/courses/college-courses/college-course-form-wrapper';
import FormikTypeahead from 'src/components/formik-typeahead';
import FormikDropdown from 'src/components/formik-dropdown';
import Label from 'shared/components/label';
import FormikTextInput from 'src/components/inputs/formik-text-input';
import InstitutionTypeaheadWithModal from 'shared/institution-typeahead/components/institution-typeahead-with-modal';
import GradeForm from 'shared/portfolio/components/courses/grade-form';

import styles from './college-course-form.module.scss';

const creditHourOptions = [0.5].concat(range(1, 13)).map((float) => ({
  value: float,
  text: float.toFixed(1),
}));

const semesterOptions = ['Fall', 'Winter', 'Spring', 'Summer'].map((sem) => ({
  value: sem,
  text: sem,
}));

const currentYear = new Date().getFullYear();

const CollegeCourseForm = () => {
  const courseOptions = useSelector((state: Store) => flatten(Object.values(state.lists.courses)));
  const formik = useFormikContext<CollegeCourseFormType>();

  return (
    <>
      <legend className="shared-required-legend" />

      <InstitutionTypeaheadWithModal
        institutionType="community-college"
        fieldName="institution"
        renderLabel={() => 'College Name'}
      />

      <div className={styles.yearTerm}>
        <Label required>
          Year Taken
          <div>
            <FormikTextInput name="year" type="number" placeholder="e.g. 2016" required />
            {!!formik.values.year &&
              currentYear - formik.values.year > 10 &&
              currentYear - formik.values.year <= 100 && (
                <div className={styles.warning}>
                  <Icon iconType="exclamation-triangle-r" className={styles.warningIcon} />{' '}
                  Depending on school guidelines courses taken over ten years ago may or may not be
                  eligible for micro-scholarships.
                </div>
              )}
          </div>
        </Label>

        <Label required>
          Term
          <FormikDropdown name="semester" options={semesterOptions} required />
        </Label>
      </div>

      <div className={styles.course}>
        <Label required>
          Course Code
          <FormikTextInput name="courseCode" placeholder="e.g. BIO 101" required />
        </Label>

        <Label required>
          Course Name
          <FormikTypeahead
            id="college-course-name-typeahead"
            name="name"
            onSelect={(value) => {
              formik.setValues({
                ...formik.values,
                subject: value.subject,
                name: value.text,
              });
            }}
            options={courseOptions}
            placeholder="e.g. Biology"
            required
          />
        </Label>
      </div>

      <div className={styles.courseInfo}>
        <Label required>
          Subject / Department
          <FormikTypeahead
            id="college-course-subject-typeahead"
            name="subject"
            onSelect={(value) => formik.setFieldValue('subject', value)}
            options={possibleMajors}
            placeholder="e.g. History"
            showCheck
            required
          />
        </Label>

        <Label required>
          Credit Hours
          <FormikDropdown
            name="creditHours"
            options={creditHourOptions}
            onChange={(e) => formik.setFieldValue('creditHours', parseIntOrNull(e.target.value))}
            required
          />
        </Label>

        <Label>
          Grade
          <GradeForm />
        </Label>
      </div>
    </>
  );
};

export default CollegeCourseForm;
